/**
 * InfoTooltip — hover/tap popover that shows a titled breakdown of the
 * numbers behind a stat card on the Customer Intelligence dashboard.
 *
 * Matches the Sales-page tooltip style Dipali approved:
 *   - Soft white card with shadow + rounded corners
 *   - Bold title, optional subtitle
 *   - One or more sections, each a label + list of rows
 *   - Each row: coloured dot · label · count · (%)
 *   - Thin separators between sections
 *
 * Usage:
 *   <InfoTooltip
 *     trigger={<StatCard ... />}
 *     title="Total Members"
 *     subtitle="Every zoho_contact row for this client"
 *     sections={[
 *       { label: 'By type × status',
 *         rows: [{ label: 'Primary · Active', value: 1237, pct: 42.7, color: '#1B2A4A' }, ...] },
 *     ]}
 *   />
 *
 * Implementation notes:
 *   - Uses Radix Popover (already installed) rather than pure CSS :hover so
 *     the popup survives a mouse-out while the user scrolls it; essential
 *     when the breakdown has 10+ rows.
 *   - `open` can be controlled by hover OR click/tap (mobile-friendly).
 *   - Colours are a curated 12-step palette that cycles — no garish
 *     auto-generated hex strings.
 */
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

const PALETTE = [
  '#1B2A4A', '#C9A66B', '#1C304A', '#6B5439', '#2A7B88',
  '#A32D2D', '#5C7A9F', '#A86B2C', '#3D3528', '#1f5736',
  '#5C5652', '#8B6F47',
];

const COLORS = {
  bg:        '#FFFFFF',
  border:    '#E5E1DC',
  ink:       '#1B2A4A',
  muted:     '#6B7280',
  rule:      '#F0ECE6',
  shadow:    '0 10px 32px rgba(11,17,32,0.18)',
};

export function InfoTooltip({
  trigger,
  title,
  subtitle,
  sections = [],
  footnote,
  width = 320,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const anchorRef = useRef(null);
  const hoverTimer = useRef(null);

  const show = () => {
    clearTimeout(hoverTimer.current);
    const r = anchorRef.current?.getBoundingClientRect();
    if (r) {
      // Try to position to the right; fall back to left if not enough space.
      const viewportW = window.innerWidth;
      const wantX = r.right + 10;
      const x = wantX + width > viewportW - 16
        ? Math.max(16, r.left - width - 10)
        : wantX;
      const y = Math.max(10, r.top);
      setCoords({ x, y });
    }
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <>
      <span
        ref={anchorRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : show(); }}
        className="inline-flex items-center cursor-help"
        data-testid={testId}>
        {trigger}
      </span>
      {open && createPortal(
        <div
          onMouseEnter={() => clearTimeout(hoverTimer.current)}
          onMouseLeave={hide}
          className="fixed rounded-lg border p-0 z-[9999] pointer-events-auto"
          style={{
            top: coords.y,
            left: coords.x,
            width,
            background: COLORS.bg,
            borderColor: COLORS.border,
            boxShadow: COLORS.shadow,
          }}
          data-testid={testId ? `${testId}-popup` : 'info-tooltip-popup'}>
          <div className="px-4 pt-3 pb-2">
            <div className="flex items-center gap-1.5">
              <Info size={11} style={{ color: '#C9A66B' }} />
              <div className="text-[12.5px] font-bold" style={{ color: COLORS.ink }}>
                {title}
              </div>
            </div>
            {subtitle && (
              <div className="text-[10.5px] mt-0.5 italic" style={{ color: COLORS.muted }}>
                {subtitle}
              </div>
            )}
          </div>
          {sections.map((s, si) => (
            <div key={si} className="px-4 py-2 border-t"
                  style={{ borderColor: COLORS.rule }}>
              {s.label && (
                <div className="text-[9.5px] tracking-[0.16em] font-bold uppercase mb-1.5"
                      style={{ color: COLORS.muted }}>
                  {s.label}
                </div>
              )}
              <div className="space-y-0.5">
                {(s.rows || []).map((r, ri) => (
                  <TooltipRow key={ri}
                               color={r.color || PALETTE[ri % PALETTE.length]}
                               label={r.label}
                               note={r.note}
                               value={r.value}
                               pct={r.pct}
                               variant={s.variant} />
                ))}
                {(!s.rows || s.rows.length === 0) && (
                  <div className="text-[11px] italic py-1"
                        style={{ color: COLORS.muted }}>
                    No data yet
                  </div>
                )}
              </div>
            </div>
          ))}
          {footnote && (
            <div className="px-4 py-2 border-t text-[10px] italic"
                  style={{ background: '#FAF7F2', borderColor: COLORS.rule,
                           color: COLORS.muted }}>
              {footnote}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

function TooltipRow({ color, label, note, value, pct, variant }) {
  const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN');
  return (
    <div className="flex items-start gap-2 text-[11.5px] py-0.5"
          style={{ color: COLORS.ink }}>
      <span className="inline-block rounded-full mt-[5px] shrink-0"
            style={{ width: 7, height: 7, background: color }} />
      <div className="flex-1 min-w-0">
        <div className="truncate">{label}</div>
        {note && (
          <div className="text-[10px] italic" style={{ color: COLORS.muted }}>
            {note}
          </div>
        )}
      </div>
      <div className="tabular-nums text-right shrink-0" style={{ minWidth: 52 }}>
        <span className="font-semibold">{fmt(value)}</span>
        {pct !== undefined && pct !== null && (
          <span className="ml-1 text-[10px]" style={{ color: COLORS.muted }}>
            ({Number(pct).toFixed(1)}%)
          </span>
        )}
      </div>
    </div>
  );
}

export default InfoTooltip;
